import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Paper, Stack, Typography } from "@mui/material";

import { formatMoney, resolveGlobalDiscount } from "../../../../shared";
import { CustomDiscountField } from "../../../../ui";
import { useEffect } from "react";

export const PriceSummarySection = () => {
  const { control, setValue, getValues } = useFormContext();
  const taxPercentage = getValues("taxPercentage");

  const [
    subtotal,
    discount,
    discountType,
    discountInput,
    taxable,
    totalIncludesTax,
    taxAmount,
    grandTotal,
  ] = useWatch({
    control,
    name: [
      "subtotal",
      "discount",
      "discountType",
      "discountInput",
      "taxable",
      "totalIncludesTax",
      "taxAmount",
      "grandTotal",
    ],
  });

  useEffect(() => {
    setValue(
      "discount",
      resolveGlobalDiscount(subtotal, discountType, discountInput)
    );
  }, [discountInput, discountType, setValue, subtotal]);

  useEffect(() => {
    if (!taxable) {
      setValue("totalIncludesTax", false);
    }
  }, [taxable, setValue]);

  useEffect(() => {
    let calculatedTaxAmount = 0;
    let calculatedGrandTotal = subtotal - discount;

    if (taxable) {
      if (totalIncludesTax) {
        calculatedTaxAmount = Math.round(
          (calculatedGrandTotal * taxPercentage) / (100 + taxPercentage)
        );
      } else {
        calculatedTaxAmount = Math.round(
          (calculatedGrandTotal * taxPercentage) / 100
        );
        calculatedGrandTotal += calculatedTaxAmount; // Tax is added on top
      }
    }

    setValue("taxAmount", calculatedTaxAmount);
    setValue("grandTotal", calculatedGrandTotal);
  }, [discount, setValue, subtotal, taxPercentage, taxable, totalIncludesTax]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        {/* Subtotal Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatMoney(subtotal)}</Typography>
        </Stack>

        {/* Discount Input */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="body2">Diskon</Typography>
          <Controller
            control={control}
            name="discountInput"
            render={({ field, fieldState }) => (
              <CustomDiscountField
                value={field.value ?? 0}
                onChange={field.onChange}
                required
                fullWidth={false}
                baseAmount={subtotal || 0}
                discountType={discountType}
                onTypeChanged={(newType) => setValue("discountType", newType)}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                inputProps={{
                  style: { textAlign: "right" },
                }}
              />
            )}
          />
        </Stack>

        {/* Tax Display */}
        {taxable && (
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="body2">Pajak ({taxPercentage}%)</Typography>
            <Typography variant="body2">{formatMoney(taxAmount)}</Typography>
          </Stack>
        )}

        {/* Grand Total Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography sx={{
            fontWeight: "bold"
          }}>Grand Total</Typography>
          <Typography
            color={grandTotal < 0 ? "error" : "inherit"}
            sx={{
              fontWeight: "bold"
            }}
          >
            {formatMoney(grandTotal)}
          </Typography>
        </Stack>
        {grandTotal < 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            Grand total tidak boleh kurang dari 0
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
